import { FC } from "react";
import {
  Navigate,
  Route,
  Routes as RouterRoutes,
  useLocation,
} from "react-router-dom";
import { FeatureFlagGate } from "./components/FeatureFlagGate";
import { SalesforceFeatureGate } from "./components/SalesforceFeatureGate";
import { getRbacClaims } from "./utils/auth";
import { IntersectionObserverProvider } from "./components/IntersectionObserver";
import { ProductCatalog } from "./pages/ProductCatalog/ProductCatalog";
import { ProductPage } from "./pages/ProductCatalog/ProductPage/ProductPage";
import { ProductSearch } from "./pages/ProductCatalog/ProductSearch/ProductSearch";
import { Workspace } from "./pages/Workspace/Workspace";
import { WorkspaceRepository } from "./pages/WorkspaceRepository/WorkspaceRepository";

import { ICPPage } from "./pages/ICP/ICPPage";
import { AttributesPage } from "./pages/ProductCatalog/AttributesPage/AttributesPage";
import { CategoryPage } from "./pages/ProductCatalog/CategoryPage/CategoryPage";
import { ProductCatalogBrowse } from "./pages/ProductCatalog/ProductCatalogBrowse";
import { VendorPage } from "./pages/ProductCatalog/VendorPage/VendorPage";
import { WorkspaceConfigurationPage } from "./pages/WorkspaceConfiguration/WorkspaceConfigurationPage";
import { CompanyPage } from "./pages/Company/CompanyPage";
import { WorkspaceLayout } from "./pages/Workspace/WorkspaceLayout";
import { SalesforceIntegrationPage } from "./pages/SalesforceIntegration/pages/SalesforceIntegrationPage";
import { SalesforceIntegrationDetailsPage } from "./pages/SalesforceIntegration/pages/SalesforceIntegrationDetailsPage";
import { AuthCallback } from "./pages/SalesforceIntegration/components/AuthCallback";
import { SettingsLayout } from "./pages/Settings/SettingsLayout";
import { CollectionsView } from "./pages/Settings/CollectionsView/CollectionsView";
import { ExportsView } from "./pages/Settings/ExportsView/ExportsView";
import { ExportDownloads } from "./pages/Downloads/ExportDownloads";
import { CompaniesExplorePage } from "./pages/Segments/Companies/CompaniesExplorePage";
import { CompaniesExploreLegacyPage } from "./pages/Segments/Companies/CompaniesExploreLegacyPage";
import { SegmentsListPage } from "./pages/Segments/SegmentsList";
import { QuotesListPage } from "./pages/Quotes";
import { ScoringProfilesPage } from "./pages/Segments/ScoringProfiles/ScoringProfilesPage";

// The new scoring-centric segments UI (topbar + drawer + matrix) is gated behind
// `ma-new-segments-ui`; until it's enabled, the legacy page renders.
const SegmentsExplore: FC = () => (
  <FeatureFlagGate
    flagKey="maNewSegmentsUi"
    fallback={<CompaniesExploreLegacyPage />}
  >
    <CompaniesExplorePage />
  </FeatureFlagGate>
);

// OAuth callback component for mk-auth
const OAuthCallback: FC = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <div>Authenticating...</div>
    </div>
  );
};

const CatalogCompaniesRedirect: FC = () => {
  const location = useLocation();
  return <Navigate to={`/segments/explore${location.search}`} replace />;
};

export const Routes: FC = () => {
  return (
    <RouterRoutes>
      <Route
        index
        path="/"
        element={<Navigate to="/segments/explore" replace />}
      />
      <Route path="/auth/callback" element={<OAuthCallback />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/workspaces" element={<Navigate to="/projects" replace />} />
      <Route path="/projects" element={<WorkspaceRepository />} />
      {/* Legacy routes - redirect to new unified projects page with correct tab */}
      <Route
        path="/projects/my"
        element={<Navigate to="/projects" replace />}
      />
      <Route
        path="/projects/public"
        element={<Navigate to="/projects?tab=org" replace />}
      />
      <Route
        path="/workspaces/my"
        element={<Navigate to="/projects" replace />}
      />
      <Route
        path="/workspaces/public"
        element={<Navigate to="/projects?tab=org" replace />}
      />
      <Route
        path="/workspaces/:id"
        element={<Navigate to="/projects/:id" replace />}
      />

      <Route path="/projects/:id" element={<WorkspaceLayout />}>
        <Route
          index
          element={
            <IntersectionObserverProvider>
              <Workspace />
            </IntersectionObserverProvider>
          }
        />
        <Route path="configuration" element={<WorkspaceConfigurationPage />} />
        <Route
          path="configuration/:tab"
          element={<WorkspaceConfigurationPage />}
        />
        <Route path="customer-profile" element={<ICPPage />} />
      </Route>

      <Route
        path="/integrations/salesforce"
        element={
          <SalesforceFeatureGate>
            <SalesforceIntegrationPage />
          </SalesforceFeatureGate>
        }
      />
      <Route
        path="/integrations/salesforce/:integrationId"
        element={
          <SalesforceFeatureGate>
            <SalesforceIntegrationDetailsPage />
          </SalesforceFeatureGate>
        }
      />
      <Route path="/auth/sfdc" element={<AuthCallback />} />
      <Route path="/settings" element={<SettingsLayout />}>
        <Route
          path="collections"
          element={
            <FeatureFlagGate
              flagKey="collectionsEnabled"
              fallback={<Navigate to="/" replace />}
            >
              <CollectionsView />
            </FeatureFlagGate>
          }
        />
        <Route
          path="exports"
          element={
            <FeatureFlagGate
              flagKey="settingsEnabled"
              fallback={<Navigate to="/" replace />}
            >
              <ExportsView />
            </FeatureFlagGate>
          }
        />
      </Route>
      <Route path="/catalog/companies" element={<CatalogCompaniesRedirect />} />
      <Route path="/catalog/companies/:companyId" element={<CompanyPage />}>
        <Route index element={null} />
        <Route path="spend" element={null} />
        <Route path="install" element={null} />
        <Route path="intent" element={null} />
        <Route path="contract" element={null} />
      </Route>
      <Route path="/downloads/export/:id" element={<ExportDownloads />} />
      <Route path="/segments" element={<SegmentsListPage />} />
      <Route
        path="/segments/scoring-profiles"
        element={
          <FeatureFlagGate
            flagKey="maScoringProfilesManager"
            fallback={<Navigate to="/segments" replace />}
          >
            <ScoringProfilesPage />
          </FeatureFlagGate>
        }
      />
      <Route
        path="/quotes"
        element={
          getRbacClaims().superKudu ? (
            <QuotesListPage />
          ) : (
            <Navigate to="/segments" replace />
          )
        }
      />
      <Route path="/segments/explore" element={<SegmentsExplore />} />
      <Route path="/segments/:segmentId" element={<SegmentsExplore />} />
      <Route path="/catalog/product" element={<ProductCatalog />}>
        <Route index element={<ProductSearch />} />
        <Route path="search" element={<ProductSearch />} />
        <Route path="product/:productId" element={<ProductPage />} />
        <Route path="vendor/:vendorId" element={<VendorPage />} />
        <Route path="category" element={<ProductCatalogBrowse />}>
          <Route index element={<CategoryPage />} />
          <Route path=":categoryId" element={<CategoryPage />} />
        </Route>
        <Route path="attribute" element={<ProductCatalogBrowse />}>
          <Route index element={<AttributesPage />} />
          <Route path=":attributeId" element={<AttributesPage />} />
        </Route>
      </Route>
    </RouterRoutes>
  );
};
